import React from "react";
import "../../assets/css/cta-contact-section.css";

const GlobeSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" />
    <ellipse cx="12" cy="12" rx="4" ry="10" stroke="currentColor" strokeWidth="1.6" />
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const ArrowRightSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="#172300"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const renderCardIcon = (icon) => {
  if (icon == null) return <GlobeSVG />;
  if (typeof icon === "string") {
    return <img src={icon} alt="" />;
  }
  if (React.isValidElement(icon)) {
    return icon;
  }
  if (typeof icon === "function") {
    const IconComponent = icon;
    return <IconComponent />;
  }
  return <GlobeSVG />;
};

const ContactCard = ({ icon, title, children }) => (
  <div className="cta-contact-section__card">
    <div className="cta-contact-section__card-icon" aria-hidden="true">
      {renderCardIcon(icon)}
    </div>
    <div className="cta-contact-section__card-content">
      <h4 className="cta-contact-section__card-label">{title}</h4>
      <div className="cta-contact-section__card-value">{children}</div>
    </div>
  </div>
);

/**
 * Props
 *  - heading        ReactNode  Main heading (string or JSX with accent spans)
 *  - subHeading     string     Sub-heading text below the heading
 *  - topRowCards    Array      Up to 4 cards on the top row. Each card: { title, content, icon? }
 *  - bottomRowCards Array      Up to 2 cards on the bottom row (omit/empty to hide)
 *  - icon           string | ReactElement | Component
 *                              Fallback icon used when a card has no `icon` of its own.
 *                              Accepts an image path (string), a JSX element, or a component.
 *  - background     string     Override the wrapper background
 *  - cta            { label, href?, onClick?, icon? }  Optional green pill button
 */
const CTAContactSection = ({
  heading,
  subHeading,
  topRowCards = defaultTopRowCards,
  bottomRowCards = defaultBottomRowCards,
  icon: defaultIcon,
  background,
  cta,
}) => {
  const wrapperStyle = background ? { background } : undefined;

  const ctaContent = cta ? (
    <>
      {cta.icon && (
        <span className="cta-contact-section__cta-icon" aria-hidden="true">
          {cta.icon}
        </span>
      )}
      <span>{cta.label}</span>
      <span className="cta-contact-section__cta-icon" aria-hidden="true">
        <img src="/images/web-new-images/new-dark-arrow.svg"/>
      </span>
    </>
  ) : null;

  return (
    <section className="cta-contact-section custom-container">
      <div className="cta-contact-section__wrapper mx-0" style={wrapperStyle}>
        <div className="cta-contact-section__blur" aria-hidden="true" />

        <div className="cta-contact-section__header">
          <h3 className="cta-contact-section__heading">
            {heading || (
              <>
                Get Your{" "}
                <span className="text-success FS40FW600">
                  Customized PTE Online Course
                </span>{" "}
                Quote Today
              </>
            )}
          </h3>
          {subHeading && (
            <p className="cta-contact-section__subheading">{subHeading}</p>
          )}
        </div>

        <div className="cta-contact-section__body">
          <div className="cta-contact-section__cards">
            <div className="cta-contact-section__row cta-contact-section__row--top">
              {topRowCards.slice(0, 4).map((card, i) => (
                <ContactCard
                  key={i}
                  icon={card.icon != null ? card.icon : defaultIcon}
                  title={card.title}
                >
                  {card.content}
                </ContactCard>
              ))}
            </div>

            {bottomRowCards && bottomRowCards.length > 0 && (
              <div className="cta-contact-section__row cta-contact-section__row--bottom">
                {bottomRowCards.slice(0, 2).map((card, i) => (
                  <ContactCard
                    key={i}
                    icon={card.icon != null ? card.icon : defaultIcon}
                    title={card.title}
                  >
                    {card.content}
                  </ContactCard>
                ))}
              </div>
            )}
          </div>

          {cta && (
            <div className="cta-contact-section__cta-wrap">
              {cta.href ? (
                <a
                  href={cta.href}
                  onClick={cta.onClick}
                  className="cta-contact-section__cta"
                >
                  {ctaContent}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={cta.onClick}
                  className="cta-contact-section__cta"
                >
                  {ctaContent}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const defaultTopRowCards = [
  { title: "Phone / WhatsApp", content: <p>+61 426 230 061</p> },
  { title: "Email", content: <p>info@languageacademy.com.au</p> },
  { title: "WhatsApp", content: <p>Quick consultation available</p> },
  { title: "Walk-in", content: <p>Visit any of our <a href="/branches" target="_blank" rel="noopener noreferrer" style={{ color: "#fff", textDecoration: "underline" }}>centres</a></p> },
];

const defaultBottomRowCards = [
  {
    title: "Consultation Hours",
    content: (
      <ul>
        <li  className="list-disc">Monday–Thursday: 9:00 AM – 5:00 PM</li>
        <li  className="list-disc">Saturday: 9:00 AM – 5:00 PM</li>
      </ul>
    ),
  },
  {
    title: "Phone / WhatsApp",
    content: (
      <ul>
        <li className="list-disc">Call +61 426 230 061 for immediate assistance</li>
        <li  className="list-disc">WhatsApp +61 426 230 061 for quick queries</li>
        <li  className="list-disc">Email info@languageacademy.com.au for detailed inquiries</li>
      </ul>
    ),
  },
];

export default CTAContactSection;
